import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import {
  DashboardHeader,
  EditableDashboardListItem,
} from '@modules/dashboard/types';
import { useGetQuantifyQueryMetadata } from '@modules/quantify/hooks';
import { useGetQuantifyTableEQLProps } from '@modules/quantify/hooks/useFetchQuantifyTableData/useGetQuantifyTableEQLProps';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Scope } from 'v2/core/entities/scope';
import { SaveAnalysisForm } from '../SaveAnalysisForm';
import { AnalysisUseCase } from 'generated/graphql';

interface SaveQuantifyFormProps {
  onAnalysisCreate?: () => void;
  dashboardToAddTo?: EditableDashboardListItem;
  setSavedAnalysisRoute?: React.Dispatch<React.SetStateAction<string | null>>;
  shouldAddToNewDashboard?: boolean;
  onClose: () => void;
}

export const SaveQuantifyForm = ({
  onAnalysisCreate,
  dashboardToAddTo,
  setSavedAnalysisRoute,
  shouldAddToNewDashboard,
  onClose,
}: SaveQuantifyFormProps) => {
  const metadata = useGetQuantifyQueryMetadata();
  const eqlProps = useGetQuantifyTableEQLProps();
  const useCase = useQuantifyStore(({ query }) =>
    query.compareTo ? AnalysisUseCase.Compare : AnalysisUseCase.Default,
  );

  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupBy?.displayName,
  );
  const shouldShowAnomalies = useQuantifyStore(
    ({ metadata }) => metadata.shouldShowAnomalies,
  );

  const { onSaveAnalysis } = useSaveAnalysis({
    eqlProps,
    metadata,
    onAnalysisCreate,
    dashboardToAddTo,
    shouldRedirect: false,
    eventProps: {
      quantifyQueryOptions: {
        showMeLabel: groupByLabel,
        shouldShowAnomalies,
      },
    },
    useCase,
  });

  const onSaveQuantifyForm = async (
    name: string,
    scope: Scope,
    newDashboard?: DashboardHeader,
  ) => {
    const analysisDetails = await onSaveAnalysis(name, scope, newDashboard);
    setSavedAnalysisRoute?.(analysisDetails.artefactRoute);

    return analysisDetails;
  };

  return (
    <SaveAnalysisForm
      onSubmit={onSaveQuantifyForm}
      useCase={useCase}
      dashboardToAddTo={dashboardToAddTo}
      shouldAddToNewDashboard={shouldAddToNewDashboard}
      onClose={onClose}
      headerText={
        shouldAddToNewDashboard
          ? 'Save Quantify and Add to a New Dashboard'
          : undefined
      }
    />
  );
};
